import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  MapPin, 
  Building2, 
  User,
  Plus,
  Clock,
  MessageSquare,
  Calendar,
  FileText,
  Send,
  Save,
  Loader2
} from 'lucide-react';
import { SendEmailModal } from '../components/SendEmailModal';

const STATUS_OPTIONS = [
  { value: 'neu', label: 'Neu' },
  { value: 'kontaktiert', label: 'Kontaktiert' },
  { value: 'termin', label: 'Termin' },
  { value: 'pilot', label: 'Pilot' },
  { value: 'laufend', label: 'Laufend' },
  { value: 'abgelehnt', label: 'Abgelehnt' }
];

const SCORE_OPTIONS = [
  { value: 'heiss', label: 'Heiß' },
  { value: 'mittel', label: 'Mittel' },
  { value: 'kalt', label: 'Kalt' }
];

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Anruf', icon: Phone },
  { value: 'email', label: 'E-Mail', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
  { value: 'note', label: 'Notiz', icon: FileText }
];

const getStatusBadgeClass = (status) => {
  const classes = {
    neu: 'bg-gray-100 text-gray-800',
    kontaktiert: 'bg-blue-100 text-blue-800',
    termin: 'bg-purple-100 text-purple-800',
    pilot: 'bg-orange-100 text-orange-800',
    laufend: 'bg-green-100 text-green-800',
    abgelehnt: 'bg-red-100 text-red-800'
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

const getScoreBadgeClass = (score) => {
  const classes = {
    heiss: 'bg-red-500 text-white',
    mittel: 'bg-orange-400 text-white',
    kalt: 'bg-blue-500 text-white'
  };
  return classes[score] || 'bg-gray-400 text-white';
};

export const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, patch, put } = useApi();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activityForm, setActivityForm] = useState({
    typ: 'call',
    beschreibung: '',
    ergebnis: '',
    dauer_minuten: '',
    naechster_schritt: ''
  });
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  const loadLead = useCallback(async () => {
    try {
      const [leadData, activitiesData] = await Promise.all([
        get(`/leads/${id}`),
        get(`/activities?lead_id=${id}`)
      ]);
      setLead(leadData);
      setActivities(activitiesData);
      setComment(leadData.notizen || '');
    } catch (error) {
      toast.error('Lead nicht gefunden');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  }, [get, id, navigate]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  const handleCallInitiated = useCallback(() => {
    // Reload activities after call is initiated
    setTimeout(() => loadLead(), 1000);
  }, [loadLead]);

  const handleStatusChange = async (newStatus) => {
    try {
      await patch(`/leads/${id}/status?status=${newStatus}`);
      setLead({ ...lead, lead_status: newStatus });
      toast.success('Status aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleSaveComment = async () => {
    setSavingComment(true);
    try {
      await put(`/leads/${id}`, { notizen: comment });
      setLead({ ...lead, notizen: comment });
      toast.success('Kommentar gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingComment(false);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      const activityData = {
        ...activityForm,
        lead_id: id,
        dauer_minuten: activityForm.dauer_minuten ? parseInt(activityForm.dauer_minuten) : null
      };
      await post('/activities', activityData);
      toast.success('Aktivität hinzugefügt');
      setDialogOpen(false);
      setActivityForm({
        typ: 'call',
        beschreibung: '',
        ergebnis: '',
        dauer_minuten: '',
        naechster_schritt: ''
      });
      loadLead();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/leads')}
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card data-testid="lead-info-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-gray-500">{lead.lead_id}</span>
                  <Badge className={getStatusBadgeClass(lead.lead_status)}>
                    {STATUS_OPTIONS.find(s => s.value === lead.lead_status)?.label}
                  </Badge>
                  <Badge className={getScoreBadgeClass(lead.lead_score)}>
                    {SCORE_OPTIONS.find(s => s.value === lead.lead_score)?.label}
                  </Badge>
                </div>
                <CardTitle className="text-2xl mt-2">{lead.firma}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Ansprechpartner</p>
                    <p className="font-medium">{lead.ansprechpartner}</p>
                    {lead.rolle && <p className="text-sm text-gray-500">{lead.rolle}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Branche</p>
                    <p className="font-medium">{lead.branche || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="font-medium">{lead.telefon || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">E-Mail</p>
                    <p className="font-medium">{lead.email || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Standort</p>
                    <p className="font-medium">{lead.standort || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Quelle</p>
                    <p className="font-medium">{lead.quelle}</p>
                  </div>
                </div>
              </div>

              {lead.notizen && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-2">Notizen</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{lead.notizen}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comment/Notes Card */}
          <Card data-testid="comment-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Kommentar / Notizen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <textarea
                  className="w-full min-h-[120px] p-3 border border-gray-200 rounded-md focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none resize-y"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Schreiben Sie hier Ihre Notizen zu diesem Lead..."
                  data-testid="comment-textarea"
                />
                <div className="flex justify-end">
                  <Button 
                    onClick={handleSaveComment}
                    disabled={savingComment || comment === (lead.notizen || '')}
                    className="bg-[#E63946] hover:bg-[#D62839]"
                    data-testid="save-comment-button"
                  >
                    {savingComment ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kommentar speichern
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activities */}
          <Card data-testid="activities-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Aktivitäten</CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setEmailModalOpen(true)}
                  data-testid="send-email-button"
                >
                  <Send className="h-4 w-4 mr-2" />
                  E-Mail senden
                </Button>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#E63946] hover:bg-[#D62839]" data-testid="add-activity-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Aktivität
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Neue Aktivität</DialogTitle>
                    <DialogDescription>
                      Dokumentieren Sie Ihre Interaktion mit diesem Lead
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddActivity} className="space-y-4" data-testid="activity-form">
                    <div className="space-y-2">
                      <Label>Typ</Label>
                      <Select 
                        value={activityForm.typ} 
                        onValueChange={(value) => setActivityForm({ ...activityForm, typ: value })}
                      >
                        <SelectTrigger data-testid="activity-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACTIVITY_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Beschreibung *</Label>
                      <textarea
                        className="w-full min-h-[100px] p-3 border border-gray-200 rounded-md focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none"
                        value={activityForm.beschreibung}
                        onChange={(e) => setActivityForm({ ...activityForm, beschreibung: e.target.value })}
                        placeholder="Was wurde besprochen?"
                        required
                        data-testid="activity-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ergebnis</Label>
                      <Input
                        value={activityForm.ergebnis}
                        onChange={(e) => setActivityForm({ ...activityForm, ergebnis: e.target.value })}
                        placeholder="z.B. Interesse bekundet, Termin vereinbart..."
                        data-testid="activity-result-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Dauer (Minuten)</Label>
                        <Input
                          type="number"
                          value={activityForm.dauer_minuten}
                          onChange={(e) => setActivityForm({ ...activityForm, dauer_minuten: e.target.value })}
                          data-testid="activity-duration-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Nächster Schritt</Label>
                      <Input
                        value={activityForm.naechster_schritt}
                        onChange={(e) => setActivityForm({ ...activityForm, naechster_schritt: e.target.value })}
                        placeholder="z.B. Follow-up am..."
                        data-testid="activity-next-step-input"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Abbrechen
                      </Button>
                      <Button type="submit" className="bg-[#E63946] hover:bg-[#D62839]" data-testid="activity-submit-button">
                        Speichern
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Noch keine Aktivitäten</p>
              ) : (
                <div className="activity-timeline">
                  {activities.map((activity) => {
                    const ActivityIcon = ACTIVITY_TYPES.find(t => t.value === activity.typ)?.icon || FileText;
                    return (
                      <div 
                        key={activity.id} 
                        className={`activity-item ${activity.typ}`}
                        data-testid={`activity-${activity.id}`}
                      >
                        <div className="bg-white p-4 rounded-lg border border-gray-100">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <ActivityIcon className="h-4 w-4 text-gray-500" />
                              <span className="font-medium">
                                {ACTIVITY_TYPES.find(t => t.value === activity.typ)?.label}
                              </span>
                              {activity.dauer_minuten && (
                                <span className="text-sm text-gray-400">
                                  • {activity.dauer_minuten} Min.
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400">
                              {formatDate(activity.created_at)}
                            </span>
                          </div>
                          <p className="mt-2 text-gray-700">{activity.beschreibung}</p>
                          {activity.ergebnis && (
                            <p className="mt-2 text-sm text-gray-600">
                              <span className="font-medium">Ergebnis:</span> {activity.ergebnis}
                            </p>
                          )}
                          {activity.naechster_schritt && (
                            <p className="mt-1 text-sm text-[#E63946]">
                              <span className="font-medium">Nächster Schritt:</span> {activity.naechster_schritt}
                            </p>
                          )}
                          <p className="mt-2 text-xs text-gray-400">
                            von {activity.created_by_name}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle className="text-lg">Status ändern</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={lead.lead_status} onValueChange={handleStatusChange}>
                <SelectTrigger data-testid="status-change-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Next Step */}
          <Card data-testid="next-step-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Nächster Schritt
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lead.naechster_schritt ? (
                <p className="text-gray-700">{lead.naechster_schritt}</p>
              ) : (
                <p className="text-gray-400 italic">Kein nächster Schritt definiert</p>
              )}
            </CardContent>
          </Card>

          {/* Timeline Info */}
          <Card data-testid="timeline-card">
            <CardHeader>
              <CardTitle className="text-lg">Zeitlinie</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Erstellt am</p>
                <p className="font-medium">{formatDate(lead.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Letzter Kontakt</p>
                <p className="font-medium">{formatDate(lead.letzter_kontakt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Letzte Aktualisierung</p>
                <p className="font-medium">{formatDate(lead.updated_at)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Send Email Modal */}
      <SendEmailModal 
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        lead={lead}
        onEmailSent={loadLead}
      />
    </div>
  );
};
